import classNames from 'classnames';
import { useCallback } from 'react';
import Dropzone from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@alkem/react-ui-button';
import { Spinner } from '@alkem/react-ui-spinner';

import Modal from 'components/ui/modal';
import i18n, { getUserLanguage } from 'utils/i18n';
import { formatDownload } from 'utils/links';

import '../TargetCatalogImportModal.scss';
import {
  closeTargetCatalogImportModal,
  selectFileInTargetCatalogImportModal,
  uploadTargetCatalog,
} from '../actions';
import { FILE_FORMAT_RANGE } from '../constants';
import {
  selectModalIsOpen,
  selectModalSelectedFile,
  selectModalSelectedFileFormat,
  selectUploadingFile,
} from '../selectors';

export const ImportActiveRangeModal = () => {
  const dispatch = useDispatch();

  const file: File | null = useSelector(selectModalSelectedFile);
  const fileFormat: string = useSelector(selectModalSelectedFileFormat);
  const modalIsOpen: boolean = useSelector(selectModalIsOpen);
  const uploadingFile: boolean = useSelector(selectUploadingFile);

  const onSelectFile = useCallback(
    (files) => {
      dispatch(selectFileInTargetCatalogImportModal(files[0]));
    },
    [dispatch],
  );

  const onClickDownloadTemplate = () => {
    let lang = getUserLanguage();
    if (lang !== 'en' && lang !== 'fr') {
      lang = 'en';
    }
    window.open(
      formatDownload(`/stream/resources/template_target_${lang}.xlsx`),
      '_blank',
    );
  };

  const uploadFile = useCallback(() => {
    dispatch(uploadTargetCatalog(FILE_FORMAT_RANGE));
  }, [dispatch]);

  const onCloseModal = useCallback(() => {
    dispatch(closeTargetCatalogImportModal());
  }, [dispatch]);

  const fileSelected = () => !!file;

  const renderRangeDropZone = () => {
    return (
      <div
        className="TargetCatalogImportModal__body"
        data-testid="RangeDropZoneID"
      >
        <div className="TargetCatalogImportModal__Readme--Step">
          <div className="TargetCatalogImportModal__Readme--Bullet">1</div>
          <span>
            {i18n.t(
              'You need to use our template in order to import your catalog',
            )}
          </span>
          <Button
            content={i18n.t('Download the template')}
            secondary
            onClick={onClickDownloadTemplate}
          />
        </div>
        <div className="TargetCatalogImportModal__Readme--Step">
          <div className="TargetCatalogImportModal__Readme--Bullet">2</div>
          <span>
            {i18n.t('Fill in the template with your catalog information')}
          </span>
        </div>
        <Dropzone multiple onDrop={onSelectFile}>
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps({
                className: classNames(
                  'TargetCatalogImportModal__dropzone',
                  isDragActive &&
                    'TargetCatalogImportModal__dropzone--actxffiive',
                ),
              })}
            >
              {uploadingFile ? (
                <Spinner big />
              ) : (
                <>
                  <input {...getInputProps()} />
                  <div className="TargetCatalogImportModal__Readme--Step">
                    <div className="TargetCatalogImportModal__Readme--Bullet">
                      3
                    </div>
                    <b>{i18n.t('Drag and drop your XLSX file here')}</b>
                  </div>
                  <div className="Dropzone__label">
                    {i18n.t('Or select a file from your computer...')}
                  </div>
                </>
              )}
            </div>
          )}
        </Dropzone>
      </div>
    );
  };

  if (!modalIsOpen || fileFormat !== FILE_FORMAT_RANGE) {
    return null;
  }

  return (
    <Modal
      modalStyle="dynamic"
      title={i18n.t('Upload catalog file')}
      confirmButtonText={i18n.t('Upload')}
      confirmDisabled={!fileSelected()}
      onConfirm={uploadFile}
      isProcessing={uploadingFile}
      onClose={onCloseModal}
    >
      <div className="TargetCatalogImportModal__body">
        {!fileSelected() &&
          fileFormat === FILE_FORMAT_RANGE &&
          renderRangeDropZone()}
        {fileSelected() && fileFormat === FILE_FORMAT_RANGE && (
          <div className="TargetCatalogImportModal_Upload">
            {i18n.t('The following file will be imported')}: {file?.name}
          </div>
        )}
      </div>
    </Modal>
  );
};
